import { useEffect, useRef } from 'react';
import type { FC, ReactNode } from 'react';

import { Placeholder, useCms, useContent, usePlaceholder, useRendering } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { toURL } from '@eneco/common/path';
import { Recaptcha } from '@eneco/components/Recaptcha/Recaptcha';
import type { RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Button, Heading, Stack } from '@eneco/sparky';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';

import type { GenericFormContent } from '../generic-form-content.types';
import { resolveSlot } from '../slots';
import { formFieldComponents } from './fields';
import { buildFormSchema } from './schema';
import type { FormValues } from './types';

interface GenericFormShellProps {
  /** Candidate names of the slot holding the form's field blocks. */
  slots: readonly string[];
  onSubmit: SubmitHandler<FormValues>;
  isSubmitting: boolean;
  isSuccess: boolean;
  buttonText?: string;
  /** Redirect after a successful send even without an address, which lands on the site root. */
  redirectWithoutHref?: boolean;
  /** Rendered below the form, e.g. a send-failure notification. */
  notification?: ReactNode;
}

/**
 * The form both `GenericForm` and `RevolutionForm` render: title, description, the field blocks in
 * their slot, reCAPTCHA and the submit button, plus the redirect on success. Their legacy components
 * share it too (`libs/sitecore/components/forms/src/GenericForm/CommonGenericForm.tsx`); each block
 * keeps its own submit endpoint and error handling.
 */
export const GenericFormShell: FC<GenericFormShellProps> = ({
  slots,
  onSubmit,
  isSubmitting,
  isSuccess,
  buttonText,
  redirectWithoutHref = false,
  notification,
}) => {
  const { adapter } = useCms();
  const content = useContent<Omit<GenericFormContent, 'senderIdOption'>>();
  const rendering = useRendering();
  const { pushHref } = useRouter();

  const recaptchaRef = useRef<RecaptchaRef>(null);

  const mainSlot = resolveSlot(rendering, slots);
  const fields = usePlaceholder(mainSlot ?? slots[0]);

  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: typedYupResolver<FormValues>(buildFormSchema(fields, formFieldComponents)),
  });
  const {
    formState: { errors },
  } = methods;

  const redirectHref = linkWithUrl(content.formSubmitRedirectLink)?.href ?? (redirectWithoutHref ? '' : undefined);

  useEffect(() => {
    if (!isSuccess || redirectHref === undefined) return;
    pushHref(toURL(redirectHref));
  }, [isSuccess, redirectHref, pushHref]);

  const description = adapter.toRichText(content.description);

  return (
    <FormProvider {...methods}>
      <RecaptchaForm onSubmit={onSubmit} recaptchaRef={recaptchaRef}>
        <Stack gap="6">
          {content.title && (
            <Heading as="h2" size="M">
              {content.title}
            </Heading>
          )}
          {description && <RichText html={description} />}
          {mainSlot ? <Placeholder name={mainSlot} /> : null}
          <Stack.Item>
            <Recaptcha hasError={Boolean(errors.recaptchaToken)} ref={recaptchaRef} />
          </Stack.Item>
          <Stack.Item>
            <Button type="submit" isLoading={isSubmitting}>
              {buttonText}
            </Button>
          </Stack.Item>
        </Stack>
      </RecaptchaForm>
      {notification}
    </FormProvider>
  );
};
